import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { app, db } from "../../firebase";
import { collection, doc, setDoc, serverTimestamp, getDocs, query } from "firebase/firestore";

const CreateCollection = () => {
  const navigate = useNavigate();
  const storage = getStorage(app);
  
  const [artists, setArtists] = useState([]);
  const [isLoadingArtists, setIsLoadingArtists] = useState(false);
  const [selectedArtistDocId, setSelectedArtistDocId] = useState("");
  const [formData, setFormData] = useState({
    artistId: "",
    name: "",
    description: "",
    yearOfProd: "",
    coverPictureFile: null,
    coverPicture: "",
  });
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Charger tous les artistes au montage du composant
  useEffect(() => {
    const fetchAllArtists = async () => {
      setIsLoadingArtists(true);
      try {
        const artistsRef = collection(db, "artists");
        const querySnapshot = await getDocs(artistsRef);
        
        const artistsList = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Ne garder que les artistes qui ont un artistId
          if (data.artistId) {
            artistsList.push({
              docId: doc.id,
              artistId: data.artistId,
              firstName: data.firstName || "",
              lastName: data.lastName || "",
            });
          }
        });

        console.log("Artistes récupérés:", artistsList.length);
        
        // Trier les artistes par artistId
        artistsList.sort((a, b) => {
          if (!a.artistId) return 1;
          if (!b.artistId) return -1;
          return a.artistId.localeCompare(b.artistId);
        });
        
        setArtists(artistsList);
        console.log("Liste des artistes après tri:", artistsList);
      } catch (error) {
        console.error("Erreur lors de la récupération de la liste des artistes:", error);
      } finally {
        setIsLoadingArtists(false);
      }
    };

    fetchAllArtists();
  }, []);

  // Mettre à jour artistId quand un artiste est sélectionné
  useEffect(() => {
    if (selectedArtistDocId) {
      const selectedArtist = artists.find((a) => a.docId === selectedArtistDocId);
      if (selectedArtist) {
        setFormData((prev) => ({
          ...prev,
          artistId: selectedArtist.artistId,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        artistId: "",
      }));
    }
  }, [selectedArtistDocId, artists]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!selectedArtistDocId) {
      alert("Veuillez d'abord sélectionner un artiste");
      return;
    }

    const selectedArtist = artists.find((a) => a.docId === selectedArtistDocId);
    if (!selectedArtist) return;

    setIsUploading(true);
    try {
      // Créer le chemin de stockage : static-artists/{artistId}/{filename}
      const storagePath = `static-artists/${selectedArtist.artistId}/${file.name}`;
      const storageRef = ref(storage, storagePath);

      // Upload du fichier
      await uploadBytes(storageRef, file);

      // Récupérer l'URL de téléchargement
      const downloadURL = await getDownloadURL(storageRef);

      // Mettre à jour le state avec l'URL
      setFormData((prev) => ({
        ...prev,
        [fieldName]: downloadURL,
      }));
    } catch (error) {
      console.error(`Erreur lors de l'upload de ${fieldName}:`, error);
      alert(`Erreur lors de l'upload: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    // Validation des champs requis
    if (!selectedArtistDocId) {
      alert("Veuillez sélectionner un artiste");
      return;
    }

    if (!formData.name || formData.name.trim() === "") {
      alert("Veuillez remplir le nom de la collection");
      return;
    }

    if (!formData.description || formData.description.trim() === "") {
      alert("Veuillez remplir la description de la collection");
      return;
    }

    if (!formData.yearOfProd || formData.yearOfProd.trim() === "") {
      alert("Veuillez remplir l'année de production");
      return;
    }

    setIsSubmitting(true);

    try {
      // Créer l'objet collection pour Firebase
      const collectionData = {
        artistId: formData.artistId,
        name: formData.name,
        description: formData.description,
        yearOfProd: formData.yearOfProd,
        coverPicture: formData.coverPicture || "",
        creationTime: serverTimestamp(),
      };

      // Créer un ID unique pour la collection basé sur le nom
      const collectionId = formData.name.replace(/\s+/g, "-").toLowerCase();
      
      // Créer la collection dans la sous-collection de l'artiste
      const collectionRef = doc(
        db,
        "artists",
        selectedArtistDocId,
        "collections",
        collectionId
      );
      
      await setDoc(collectionRef, collectionData);

      console.log("Collection créée:", collectionData);
      
      // Rediriger vers la page de détails de la collection
      navigate(`/collection/${selectedArtistDocId}/${collectionId}`);
    } catch (error) {
      console.error("Erreur lors de la création de la collection:", error);
      alert(`Erreur lors de la création de la collection: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#00263a]">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <h1 className="text-3xl font-bold text-white mb-6">Créer une Collection</h1>

        {/* Informations de base */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Informations de base</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Artiste *
              </label>
              <select
                value={selectedArtistDocId}
                onChange={(e) => setSelectedArtistDocId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                style={{ colorScheme: 'dark' }}
                disabled={isLoadingArtists || artists.length === 0}
              >
                <option value="">
                  {isLoadingArtists 
                    ? "Chargement des artistes..." 
                    : artists.length === 0 
                    ? "Aucun artiste disponible" 
                    : "Sélectionner un artiste"}
                </option>
                {artists.length > 0 && artists.map((artist) => (
                  <option key={artist.docId} value={artist.docId}>
                    {artist.artistId ? artist.artistId.replaceAll("-", " ") : artist.docId} ({artist.firstName || ""} {artist.lastName || ""})
                  </option>
                ))}
              </select>
              {!isLoadingArtists && artists.length === 0 && (
                <p className="mt-2 text-sm text-yellow-400">
                  Aucun artiste disponible. Veuillez créer un artiste d'abord.
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Artist ID (généré automatiquement)
              </label>
              <input
                type="text"
                value={formData.artistId}
                disabled
                className="w-full px-3 py-2 border border-gray-600 bg-gray-600 text-gray-400 rounded-md cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Nom de la collection *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                placeholder="ex: Portraits of Benin"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Année de production *
              </label>
              <input
                type="text"
                name="yearOfProd"
                value={formData.yearOfProd}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                placeholder="ex: 2024"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-white mb-1">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="6"
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                placeholder="Description de la collection..."
              />
            </div>
          </div>
        </div>

        {/* Image */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Image de couverture</h2>
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Cover Picture
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, "coverPicture")}
              className="block w-full text-sm text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#33c7b6] file:text-[#00263a] hover:file:bg-[#2ab3a3] cursor-pointer"
              disabled={!selectedArtistDocId || isUploading}
            />
            {formData.coverPicture && (
              <div className="mt-4">
                <p className="text-xs text-green-400 mb-2">✓ Uploadé</p>
                <img
                  src={formData.coverPicture}
                  alt="Cover preview"
                  className="w-full max-w-md h-48 object-cover rounded-md"
                />
              </div>
            )}
            {isUploading && (
              <p className="mt-2 text-xs text-yellow-400">Upload en cours...</p>
            )}
          </div>
        </div>

        {/* Bouton Confirmer */}
        <div className="flex justify-center">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || isUploading}
            className={`${
              isSubmitting || isUploading
                ? "bg-gray-600 cursor-not-allowed"
                : "bg-[#33c7b6] hover:bg-[#2ab3a3]"
            } text-[#00263a] px-8 py-3 rounded-full font-semibold transition-colors`}
          >
            {isSubmitting ? "Création en cours..." : "Confirmer"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateCollection;

